import {match} from 'ts-pattern';
import type {StoreApi} from 'zustand/vanilla';

import type {EagGridState, EagGridStore} from '../createEagGridStore';

type SelectionActions = Pick<
  EagGridStore,
  | 'setSelectedRowIds'
  | 'selectAllFiltered'
  | 'toggleRowSelection'
  | 'selectAll'
  | 'deselectAll'
  | 'setSingleSelection'
>;

/** Selection actions. Bodies moved verbatim from createEagGridStore.ts. */
export function createSelectionActions(
  store: StoreApi<EagGridState>,
  selectAllFilteredSelected: (state: EagGridState) => boolean
): SelectionActions {
  return {
    setSelectedRowIds: (selectedRowIds: Set<string>) => {
      store.setState({selectedRowIds, bulkSelectionQueryId: null});
    },

    selectAllFiltered: () => {
      const state = store.getState();
      if (
        state.fetchState !== 'success' ||
        state.queryId === '' ||
        selectAllFilteredSelected(state)
      ) {
        return;
      }
      store.setState({
        bulkSelectionQueryId: state.queryId,
        selectedRowIds: new Set(state.rows.map((row) => row.id)),
      });
    },

    toggleRowSelection: (id: string) => {
      const state = store.getState();
      const next = selectAllFilteredSelected(state)
        ? new Set(state.rows.map((row) => row.id))
        : new Set(state.selectedRowIds);
      match(next.has(id))
        .with(true, () => next.delete(id))
        .with(false, () => next.add(id))
        .exhaustive();
      store.setState({selectedRowIds: next, bulkSelectionQueryId: null});
    },

    selectAll: (ids: string[]) => {
      store.setState({selectedRowIds: new Set(ids), bulkSelectionQueryId: null});
    },

    deselectAll: () => {
      if (
        store.getState().selectedRowIds.size === 0 &&
        store.getState().bulkSelectionQueryId === null
      ) {
        return;
      }
      store.setState({selectedRowIds: new Set(), bulkSelectionQueryId: null});
    },

    setSingleSelection: (id: string) => {
      // SINGLE-mode policy lives in two places: this replace-on-click (with toggle-off)
      // and the click-parity normalization in utils/resolveExternalRowSelection.ts for
      // external requests (which deliberately has NO toggle — a declarative request for
      // an already-selected row must stay a no-op). Keep the two in sync when changing
      // SINGLE semantics.
      const current = store.getState().selectedRowIds;
      // Toggle off if already the only selected row
      if (current.size === 1 && current.has(id)) {
        store.setState({selectedRowIds: new Set(), bulkSelectionQueryId: null});
        return;
      }
      store.setState({selectedRowIds: new Set([id]), bulkSelectionQueryId: null});
    },
  };
}
